// Considered: EditorHeaderMolecule — rejected because it has an icon back button and a static title; this header needs an
// inline-editable name and description, lifecycle mode, weight, a status chip and the ⋯ menu. See spec Reuse Audit.
import React, { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { FactorMode, StatusValue } from 'types/spherePoints';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import InputNumberAtom from '../../atoms/InputNumber/InputNumberAtom';
import MenuAtom, { type MenuHandle, type MenuItem } from '../../atoms/menu/menu';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import './FactorIdentityHeader.scss';

export type FactorIdentityKind = 'manual' | 'related_spheres' | 'arc_health';

const KIND_ICON: Record<FactorIdentityKind, string> = {
  manual: 'pi pi-pencil',
  related_spheres: 'pi pi-sitemap',
  arc_health: 'pi pi-heart',
};

const KIND_LABEL_KEY: Record<FactorIdentityKind, string> = {
  manual: 'statusFactors.detail.kind.manual',
  related_spheres: 'statusFactors.detail.kind.related_spheres',
  arc_health: 'statusFactors.detail.kind.arc_health',
};

/** Enough fraction digits that no stored weight is rounded for display; a rounded display re-parses on blur and rewrites the value. */
const WEIGHT_PRECISION = 3;

const MODES: FactorMode[] = ['active', 'observing', 'paused'];

export interface FactorIdentityHeaderProps {
  kind: FactorIdentityKind;
  name: string;
  description: string;
  /** Weight of this factor in its sphere, 0–1. */
  weight: number;
  mode: FactorMode;
  /** Current health status; null reads as Unknown. */
  status: StatusValue | null;
  showOnCards: boolean;
  /** One edit gate: false → read-only fields and no ⋯ menu. */
  canEdit: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onWeightChange: (weight: number) => void;
  onModeChange: (mode: FactorMode) => void | Promise<void>;
  /** Saves immediately — not part of the draft. */
  onShowOnCardsChange: (next: boolean) => void;
  /** Opens the existing delete confirm. */
  onDelete: () => void;
  onBack?: () => void;
  /** New factors have no summary to go back to; callers pass "Cancel". */
  backLabel?: string;
  modeBusy?: boolean;
  /** The Show on cards PATCH is in flight; the menu item is disabled until it settles. */
  showOnCardsBusy?: boolean;
  errors?: { name?: string; description?: string; weight?: string };
  className?: string;
}

const FactorIdentityHeader: React.FC<FactorIdentityHeaderProps> = ({
  kind,
  name,
  description,
  weight,
  mode,
  status,
  showOnCards,
  canEdit,
  onNameChange,
  onDescriptionChange,
  onWeightChange,
  onModeChange,
  onShowOnCardsChange,
  onDelete,
  onBack,
  backLabel,
  modeBusy = false,
  showOnCardsBusy = false,
  errors = {},
  className,
}) => {
  const { t } = useTranslation();
  const uid = useId();
  const menuRef = useRef<MenuHandle>(null);
  const moreRef = useRef<HTMLSpanElement>(null);
  const nameId = `${uid}-name`;
  const descriptionId = `${uid}-description`;
  const weightLabelId = `${uid}-weight-label`;
  const weightErrorId = `${uid}-weight-error`;
  const nameErrorId = `${uid}-name-error`;
  const descriptionErrorId = `${uid}-description-error`;
  const menuId = `${uid}-menu`;
  const [menuOpen, setMenuOpen] = useState(false);
  const factorLabel = name || t(KIND_LABEL_KEY[kind]);
  const shownStatus: StatusValue = status ?? 'unknown';

  const modeOptions = useMemo(
    () => MODES.map((value) => ({ value, label: t(`statusFactors.mode.${value}`) })),
    [t],
  );

  const menuModel: MenuItem[] = [
    {
      label: t('statusFactors.showOnCards.label'),
      checked: showOnCards,
      disabled: showOnCardsBusy,
      command: () => onShowOnCardsChange(!showOnCards),
    },
    { separator: true },
    {
      label: t('statusFactors.header.deleteFactor'),
      icon: 'pi pi-trash',
      className: 'factor-identity-header__delete-item',
      command: onDelete,
    },
  ];

  return (
    <header className={['factor-identity-header', className].filter(Boolean).join(' ')} data-testid="factor-identity-header">
      {onBack && (
        <button type="button" className="factor-identity-header__back" onClick={onBack}>
          <span aria-hidden="true">‹ </span>
          {backLabel ?? t('statusFactors.actions.backToSummary')}
        </button>
      )}
      <div className="factor-identity-header__eyebrow">
        <StatusBadgeAtom tone="info" icon={KIND_ICON[kind]} label={t(KIND_LABEL_KEY[kind])} data-testid="factor-identity-kind" />
        <StatusBadgeAtom
          variant="measured"
          statusValue={shownStatus}
          label={t(`statusVocabulary.${shownStatus}`)}
          data-testid="factor-identity-status"
        />
      </div>
      <div className="factor-identity-header__row">
        <div className="factor-identity-header__text">
          <InputAtom
            id={nameId}
            appearance="inline"
            className="factor-identity-header__name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={t(KIND_LABEL_KEY[kind])}
            aria-label={t('statusFactors.fields.name', 'Name')}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? nameErrorId : undefined}
            readOnly={!canEdit}
          />
          {errors.name && (
            <small id={nameErrorId} className="form-field__error">
              {errors.name}
            </small>
          )}
          <InputAtom
            id={descriptionId}
            appearance="inline"
            className="factor-identity-header__description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder={t('statusFactors.header.descriptionPlaceholder')}
            aria-label={t('statusFactors.fields.description', 'Description')}
            aria-invalid={!!errors.description}
            aria-describedby={errors.description ? descriptionErrorId : undefined}
            readOnly={!canEdit}
          />
          {errors.description && (
            <small id={descriptionErrorId} className="form-field__error">
              {errors.description}
            </small>
          )}
        </div>
        <div className="factor-identity-header__controls">
          <SegmentedControlAtom
            mode="single"
            variant="strip"
            tone="quiet"
            size="small"
            options={modeOptions.map((o) => ({ ...o, disabled: !canEdit || modeBusy }))}
            value={mode}
            onChange={(next) => {
              if (next !== mode) void onModeChange(next as FactorMode);
            }}
            ariaLabel={t('statusFactors.mode.ariaLabel')}
          />
          <div className="factor-identity-header__weight">
            <span id={weightLabelId} className="factor-identity-header__weight-label">
              {t('statusFactors.header.weightInSphere')}
            </span>
            <InputNumberAtom
              value={weight}
              onChange={(next) => {
                if (next === null) return;
                const clamped = Math.max(0, Math.min(1, next));
                if (clamped !== weight) onWeightChange(clamped);
              }}
              min={0}
              max={1}
              step={0.1}
              precision={WEIGHT_PRECISION}
              size="compact"
              disabled={!canEdit}
              invalid={!!errors.weight}
              ariaLabelledBy={weightLabelId}
              ariaDescribedBy={errors.weight ? weightErrorId : undefined}
              className="factor-identity-header__weight-input"
            />
          </div>
          {canEdit && (
            <span ref={moreRef} className="factor-identity-header__more">
              <ButtonAtom
                variant="ghost"
                shape="icon-square"
                icon="pi pi-ellipsis-h"
                aria-label={t('statusFactors.header.moreActions', { factor: factorLabel })}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-controls={menuId}
                onClick={(e) => menuRef.current?.toggle(e)}
              />
              <MenuAtom
                popup
                model={menuModel}
                ref={menuRef}
                id={menuId}
                onShow={() => setMenuOpen(true)}
                onHide={() => {
                  setMenuOpen(false);
                  moreRef.current?.querySelector('button')?.focus();
                }}
              />
            </span>
          )}
        </div>
      </div>
      {errors.weight && (
        <small id={weightErrorId} className="form-field__error factor-identity-header__weight-error">
          {errors.weight}
        </small>
      )}
    </header>
  );
};

FactorIdentityHeader.displayName = 'FactorIdentityHeader';

export default FactorIdentityHeader;
